import { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useCalendlyConfig } from '../../hooks/useCalendlyConfig';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
      }) => void;
    };
  }
}

export function CalendarApp() {
  const { chatbotPublicId } = useChatbot();
  const { config, initialized, initializeConfig } = useCalendlyConfig();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  // Initialize the configuration when the component mounts
  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initializeConfig(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initializeConfig]);

  // Load the Calendly script
  useEffect(() => {
    if (!config.url) return;
    
    if (!document.getElementById('calendly-script') && !window.Calendly) {
      const script = document.createElement('script');
      script.id = 'calendly-script';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => setScriptError(true);
      document.body.appendChild(script);
    } else if (window.Calendly) {
      setScriptLoaded(true);
    }

    return () => {
      const script = document.getElementById('calendly-script');
      if (script) {
        script.remove();
      }
    };
  }, [config.url]);

  // Initialize the widget once the script is loaded
  useEffect(() => {
    if (scriptLoaded && config.url) {
      const element = document.getElementById('calendly-widget');
      if (element && window.Calendly) {
        window.Calendly.initInlineWidget({
          url: config.url,
          parentElement: element
        });
      }
    }
  }, [scriptLoaded, config.url]);

  if (!config.url) {
    return (
      <div className="text-center text-gray-500 p-4">
       No Calendly link configured. Please set up your Calendly link in the settings.
      </div>
    );
  }

  if (scriptError) {
    return (
      <div className="text-center text-red-500 p-4">
        Unable to load the Calendly widget. Please check your internet connection and try again.
      </div>
    );
  }

  return (
    <div className="w-full h-[500px] overflow-hidden rounded-lg">
      {!scriptLoaded && (
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <div id="calendly-widget" className="w-full h-full" />
    </div>
  );
}